import dayjs from "dayjs";
import duration from 'dayjs/plugin/duration';
dayjs.extend(duration);

function durationBetweenDates(fromDate, toDate) {
    const start = dayjs(Number(fromDate));
    const end = dayjs(Number(toDate));

    const totalMonths = end.diff(start, 'month');

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    if (years > 0 && months > 0) {
        return `${years}y ${months}m`;
    }

    if (years > 0) {
        return `${years}y`;
    }

    if (months > 0) {
        return `${months}m`;
    }
}

function formatNumber(number, isCurrency) {
    if (typeof number === "string") {
        number = Number(number);
    }

    if (isNaN(number) || number == 0) {
        return '-';
    }

    const locale = isCurrency ? 'en-IN' : 'en-US';

    return (
        Intl.NumberFormat(locale, {
            style: isCurrency ? 'currency' : 'decimal',
            currency: 'INR',
            currencyDisplay: 'symbol',
            notation: isCurrency ? 'standard' : 'compact',
            maximumFractionDigits: isCurrency ? 0 : 1
        }).format(number)
    );
}

// Backend consolidated the old scalar `number`/`contact_number`/`newNumber` fields into
// a single `contactNumbers: [{ type, name, countryCode, number, isPrimary }]` array.
// Many routes still keep returning a legacy `.number` string for back-compat, but some
// (e.g. users/viewedInfluencers, users/exportcsv, finance/getbycampaignid) now only send
// `contactNumbers`. This helper works against either shape so callers don't have to guess
// which one a given API response used.
function getPrimaryContactNumber(record) {
    if (!record) return null;
    if (record.number) return record.number;
    const contacts = record.contactNumbers;
    if (!Array.isArray(contacts) || contacts.length === 0) return null;
    const primary = contacts.find((c) => c && c.isPrimary) || contacts[0];
    return primary?.number || null;
}

// Same rule as the backend's plugin/viewInfluencerDetails: the LAST active manager
// that has a manager or agency name. Keep these in sync so a contact shows the same
// manager right after unlocking and after a reload.
function getActiveManager(record) {
    const managers = Array.isArray(record?.manager) ? record.manager : [];
    for (let i = managers.length - 1; i >= 0; i--) {
        const m = managers[i];
        if (m && m.isActive === true && (m.manager_name || m.agency_name)) return m;
    }
    return {};
}

// Normalizes a comma-joined number string ("n1,n2") or an array into "n1, n2".
function formatPhoneList(value) {
    const parts = Array.isArray(value) ? value : String(value ?? '').split(',');
    return parts.map((p) => String(p ?? '').trim()).filter(Boolean).join(', ');
}

// Phone to display for an unlocked contact, matching what the unlock endpoint
// returns: the active manager's number, else every number on file.
function getContactPhone(record) {
    const manager = getActiveManager(record);
    if (manager.manager_number) return formatPhoneList(manager.manager_number);
    if (Array.isArray(record?.contactNumbers) && record.contactNumbers.length) {
        return formatPhoneList(record.contactNumbers.map((c) => c?.number));
    }
    return formatPhoneList(record?.number);
}

function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
}

// Backend consolidated the old categoryName/category/category_name fields into a flat
// `categories: [String]` array. Some routes still project a back-compat joined
// `categoryName` string, others only send the raw array now (e.g. users/exportcsv) —
// this normalizes either shape into a single display string.
function getCategoryDisplay(record) {
    if (!record) return '';
    if (typeof record.categoryName === 'string' && record.categoryName) return record.categoryName;
    if (Array.isArray(record.categories) && record.categories.length) return record.categories.join(', ');
    if (typeof record.category_name === 'string' && record.category_name) return record.category_name;
    return '';
}

// A proxy/load-balancer error (502/503/504) in front of the API can return an
// HTML error page as the response body — a non-empty string, but not
// anything worth dumping into an <Alert>. Caught by shape rather than
// content-type, since axios doesn't reliably expose the response header here.
function looksLikeHtml(value) {
    const trimmed = value.trim();
    return trimmed.startsWith("<") || trimmed.length > 300;
}

// Turns an axios rejection into one line worth showing a user directly —
// the backend's plain-text 403/400 messages, or a JSON {message} body, or
// the network-level error.message as a last resort. Used by report pages
// where a silently-swallowed error would otherwise look identical to a
// legitimately empty result.
function describeApiError(error) {
    const data = error?.response?.data;
    if (typeof data === "string" && data && !looksLikeHtml(data)) return data;
    if (typeof data?.message === "string" && data.message) return data.message;
    return error?.message || "Something went wrong";
}

const helpers = {
    durationBetweenDates,
    formatNumber,
    getPrimaryContactNumber,
    getActiveManager,
    getContactPhone,
    formatPhoneList,
    escapeHtml,
    getCategoryDisplay,
    describeApiError,
}

export default helpers;